<template>
  <TableList
    v-rect-loading="loading"
    class="open-question-template-group-list"
    row-key="_id"
    :default-expand-all="false"
    :expand-row-keys="expandedGroupKeys"
    :title="$t(`${i18nPrefix}.template.title`)"
    :row-class-name="getRowClassName"
    :listData="groupListSearchResult"
    :tableColumns="tableColumns"
    :dragSortFunc="dragSortFunc"
    :span-method="spanMethod"
    :action-btn="actionBtn"
    action-width="185"
    @action="handleAction"
    @expand-change="handleExpandChange"
  >
    <template #filter>
      <el-select
        v-model="search.groupId"
        filterable
        clearable
        class="group-filter"
        :placeholder="$t(`${i18nPrefix}.placeholder.groupFilter`)"
        @change="searchGroupList"
      >
        <el-option
          v-for="(item, index) in groupList"
          :key="index"
          :label="item.i18n_name"
          :value="item.id"
        />
      </el-select>
    </template>
    <template #btn>
      <el-button class="create-group-btn create-btn" @click="toTaskHistoryPage">{{ $t(`${i18nPrefix}.btn.taskList`) }}</el-button>
      <el-button class="create-group-btn create-btn" @click="openAddGroupDialog">{{ $t(`${i18nPrefix}.btn.addNewGroup`) }}</el-button>
      <el-button class="create-question-btn create-btn" @click="openAddQuestionDialog">{{ $t(`${i18nPrefix}.btn.addNewQuestion`) }}</el-button>
    </template>
    <template #groupName="{row, formattedData}">
      <div class="other-group" v-if="row.id === -1">
        <span class="title">{{ $t(`${i18nPrefix}.label.otherGroup`) }}</span>
      </div>
      <span v-else>{{ formattedData }}</span>
    </template>
    <template #questionTitle="{ row, formattedData }">
      <div v-if="row._vType === DATA_TYPE.QUESTION" class="question-tree-title" :class="{ 'is-tree-question': row._isTreeNode, 'is-child-question': (row._treeDepth || 0) > 0 }">
        <div v-if="row._treeParentKey" class="question-tree-meta">
          <span class="tree-meta-gutter" aria-hidden="true">
            <span
              v-for="slotIndex in Math.max(row._treeDepth - 1, 0)"
              :key="`meta-${slotIndex}`"
              class="tree-gutter-slot"
            ></span>
          </span>
          <span class="tree-trigger-label">{{ getTreeTriggerLabel(row) }}</span>
        </div>
        <div class="question-title-line">
          <span v-if="row._treeParentKey" class="tree-gutter" aria-hidden="true">
            <span
              v-for="slotIndex in row._treeDepth"
              :key="`title-${slotIndex}`"
              class="tree-gutter-slot"
              :class="{ 'tree-connector': slotIndex === row._treeDepth }"
            ></span>
          </span>
          <span class="question-title-text">{{ formattedData }}<span v-if="row.required" class="required-asterisk" aria-hidden="true">*</span></span>
        </div>
      </div>
    </template>
    <template #draggable="{row}">
      <div class="drag-btn">
        <i v-if="canDragQuestion(row)" class="iconfont iconMore draggable-handle"></i>
      </div>
    </template>
    <template #status="{ data, row }">
      <el-tooltip v-if="isTreeStatusLocked(row)" :content="$t(`${i18nPrefix}.message.treeStatusChangeNotAllowed`)" placement="top">
        <span class="locked-status"><el-switch :value="data === 1" disabled></el-switch></span>
      </el-tooltip>
      <el-switch v-else :value="data === 1" @change="handleStatusChange(row)"></el-switch>
    </template>
    <template #required="{ data, row }">
      <el-switch
        v-if="row._vType === DATA_TYPE.QUESTION"
        :value="Boolean(data)"
        :aria-label="$t('admin.company.questionnaireManagement.label.required')"
        @change="handleRequiredChange(row, $event)"
      ></el-switch>
    </template>
    <template #page-bottom>
      <DialogWrapper
        class="editor-dialog editor-dialog"
        :loading="groupEditor.loading"
        :show.sync="groupEditor.show"
        :close-on-click-modal="false"
        :title="$t(`${i18nPrefix}.editor.group.title.${groupEditor.isEdit ? 'edit' : 'add'}`)"
        @cancel="handleCancelGroup"
        @save="handleSaveGroup"
      >
        <GroupEditor ref="groupEditor" :is-edit="groupEditor.isEdit" :group="groupEditor.group"></GroupEditor>
      </DialogWrapper>
      <DialogWrapper
        class="editor-dialog question-editor"
        :show.sync="directQuestionEditor.show"
        :loading="directQuestionEditor.loading"
        :close-on-click-modal="false"
        :title="$t(`${i18nPrefix}.editor.question.title.${directQuestionEditor.isEdit ? 'edit' : 'add'}`)"
        @save="saveDirectQuestion"
      >
        <QuestionEditor
          ref="directQuestionEditor"
          is-template
          :is-edit="directQuestionEditor.isEdit"
          :question="directQuestionEditor.question"
          :group-list="groupList"
          :disabled-template="directQuestionEditor.templateLocked"
          :disabled-question-type="disabledQuestionType"
          :disable-question-type="hasQuestionChildren(directQuestionEditor.question)"
          show-required-asterisk
        />
      </DialogWrapper>
      <DialogWrapper
        class="editor-dialog question-editor"
        :show.sync="childQuestionEditor.show"
        :loading="childQuestionEditor.loading"
        :close-on-click-modal="false"
        :title="$t(`${i18nPrefix}.editor.subQuestion.title.${childQuestionEditor.isEdit ? 'edit' : 'add'}`)"
        @save="saveDirectChildQuestion"
      >
        <QuestionEditor
          ref="directChildQuestionEditor"
          :is-edit="childQuestionEditor.isEdit"
          :question="childQuestionEditor.question"
          :disabled-question-type="disabledQuestionType"
          :disable-question-type="hasQuestionChildren(childQuestionEditor.question)"
          :question-type-label="$t(`${i18nPrefix}.label.subQuestionType`)"
          :relation-fields="childRelationFields"
          show-required-asterisk
          @relation-trigger-option-change="handleChildTriggerOptionChange"
        />
      </DialogWrapper>
      <ConfirmDialog
        append-to-body
        :show.sync="deleteConfirmation.show"
        :loading="deleteConfirmation.loading"
        :title="$t('common.message.warning')"
        :content="$t(`${i18nPrefix}.message.confirmDeleteQuestionCount`, { count: deleteConfirmation.count })"
        save-btn-label-i18n-key="common.btn.delete"
        @confirm="confirmDeleteQuestionTree"
        @cancel="cancelDeleteQuestionTree"
      />
    </template>
  </TableList>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import GroupEditor from '@/components/openQuestion/groupEditor.vue'
import QuestionEditor from '@/components/openQuestion/questionEditor.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import ConfirmDialog from '@/components/dialog/confirmDialog.vue'
import { QUESTION_TYPE, DATA_TYPE } from '@/constant/openQuestion'
import { i18nPrefix, parseGroup, parseQuestion } from '@/components/openQuestion/common'
import {
  MAX_TREE_DEPTH,
  decorateQuestionTreeRows,
  getOptionChildKey,
  getQuestionKey,
  removeQuestionSubtree,
  reorderRootSubtrees
} from '@/utils/openQuestionTree'

export function assignMissingOptionVids (questionKey, options = []) {
  return options.map((option, index) => {
    if (option.id != null || option._vid != null) return option
    return { ...option, _vid: `${questionKey}_opt_${index}` }
  })
}

export default {
  components: {
    TableList,
    GroupEditor,
    QuestionEditor,
    DialogWrapper,
    ConfirmDialog
  },
  data () {
    return {
      i18nPrefix,
      DATA_TYPE,
      QUESTION_TYPE,
      MAX_TREE_DEPTH,
      loading: false,
      groupList: [],
      groupListSearchResult: [],
      expandedGroupKeys: [],
      hasInitializedExpandedGroups: false,
      search: {
        groupId: null
      },
      groupEditor: {
        loading: false,
        show: false,
        isEdit: false,
        group: null
      },
      deleteConfirmation: { show: false, loading: false, count: 0, pending: null },
      directQuestionEditor: { show: false, loading: false, isEdit: false, question: null, groupId: null, component: [], originalQuestionIds: [], templateLocked: false },
      childQuestionEditor: { show: false, loading: false, isEdit: false, question: null, parent: null, groupId: null, optionKey: null, originalOptionKey: null, component: [], originalQuestionIds: [] }
    }
  },
  computed: {
    tableColumns () {
      const vm = this
      const columns = [
        {
          width: 153,
          cellClass: 'management-group-cell',
          label: this.$t(`${this.i18nPrefix}.label.groupName`),
          slotName: 'groupName',
          dataKey: 'i18n_name',
          formatter: (data, row) => {
            if (row._vType === DATA_TYPE.GROUP) {
              return data
            }
          }
        },
        {
          width: 111,
          cellClass: 'management-type-cell',
          label: this.$t(`${this.i18nPrefix}.label.questionType`),
          dataKey: 'type',
          formatter: (data, row) => {
            if (row._vType === DATA_TYPE.QUESTION) {
              const type = _.findKey(QUESTION_TYPE, item => item === data)
              return vm.$t(`${vm.i18nPrefix}.questionType.${type}`)
            }
          }
        },
        {
          width: 371,
          cellClass: 'management-title-cell',
          label: this.$t(`${this.i18nPrefix}.label.questionTitle`),
          dataKey: 'title',
          slotName: 'questionTitle',
          formatter: (data, row) => {
            if (row._vType === DATA_TYPE.QUESTION) {
              return vm.getQuestionTitle(row)
            }
          }
        },
        {
          label: this.$t('admin.company.questionnaireManagement.label.required'),
          dataKey: 'required',
          slotName: 'required',
          cellClass: 'management-required-cell',
          width: 92
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.status`),
          dataKey: 'status',
          slotName: 'status',
          cellClass: 'management-status-cell',
          width: 92
        },
        {
          label: this.$t('common.label.sortOperate'),
          isDragSort: true,
          slotName: 'draggable',
          cellClass: 'management-sort-cell',
          width: 92
        }
      ]
      return columns
    },
    actionBtn () {
      return {
        edit: { show: true },
        addSubQuestion: {
          labelI18nKey: `${this.i18nPrefix}.btn.addSubQuestion`,
          show: row => {
            const hasMessage = Boolean(this.getAddSubQuestionDisabledMessage(row))
            return !hasMessage
          },
          disabled: row => Boolean(this.getAddSubQuestionDisabledMessage(row)),
          tooltip: row => this.getAddSubQuestionDisabledMessage(row)
        },
        delete: { show: row => row._vType === DATA_TYPE.QUESTION }
      }
    },
    childTriggerOptions () {
      const editor = this.childQuestionEditor
      const parent = editor.parent
      if (!parent) return []
      return _.chain(parent.options || []).filter(option => {
        const key = String(option.id || option._vid)
        const childKey = this.getOptionChildKey(option)
        return !option.is_knockout && (!childKey || key === String(editor.originalOptionKey))
      }).map(option => ({ key: String(option.id || option._vid), label: this.getOptionDescription(option) })).value()
    },
    childRelationFields () {
      return {
        parentQuestion: {
          value: this.getQuestionTitle(this.childQuestionEditor.parent),
          disabled: true
        },
        triggerOption: {
          value: this.childQuestionEditor.optionKey,
          control: 'select',
          filterable: true,
          options: this.childTriggerOptions,
          required: true
        }
      }
    },
    flatGroupQuestionList () {
      const flatList = []
      _.forEach(this.groupListSearchResult, group => {
        flatList.push(group)
        flatList.push(...group.children)
      })
      return flatList
    },
    visibleGroupQuestionList () {
      const flatList = []
      const expandedKeys = new Set(this.expandedGroupKeys)
      _.forEach(this.groupListSearchResult, group => {
        flatList.push(group)
        if (expandedKeys.has(group._id)) {
          flatList.push(...group.children)
        }
      })
      return flatList
    },
    hasVideoQuestionPermission () {
      return this.hasAnyPermission('PREMIUM_UPLOAD_VIDEO')
    },
    hasAttachmentQuestionPermission () {
      return this.hasAnyPermission('PREMIUM_UPLOAD_PORTFOLIO')
    },
    disabledQuestionType () {
      const disabledQuestionType = []
      if (!this.hasVideoQuestionPermission) {
        disabledQuestionType.push(QUESTION_TYPE.VIDEO)
      }
      if (!this.hasAttachmentQuestionPermission) {
        disabledQuestionType.push(QUESTION_TYPE.ATTACHMENT)
      }
      return disabledQuestionType
    }
  },
  created () {
    this.fetchGroupList()
  },
  mounted () {

  },
  methods: {
    ...mapActions('openQuestion', [
      'getQuestionTemplateGroupList',
      'createQuestionTemplateGroup',
      'updateQuestionTemplateGroup',
      'createQuestionTemplate',
      'updateQuestionTemplate',
      'saveQuestionTemplateTree',
      'moveGroupQuestionTemplate'
    ]),
    handleChildTriggerOptionChange (optionKey) {
      this.childQuestionEditor.optionKey = optionKey
    },
    async dragSortFunc (newIndex, oldIndex, tableSorter, event) {
      if (newIndex === oldIndex) {
        return
      }
      const oldData = this.findDragRow(event && event.item) || this.visibleGroupQuestionList[oldIndex]
      const previousData = this.findDragRow(event && event.item && event.item.previousElementSibling)
      const nextData = this.findDragRow(event && event.item && event.item.nextElementSibling)
      this.restoreDragDom(tableSorter, newIndex, oldIndex)
      if (!oldData || oldData._vType !== DATA_TYPE.QUESTION || !previousData) return

      if (this.isInsideQuestionTreeBoundary(previousData, nextData)) {
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.treeDropInsideNotAllowed`))
        return
      }

      const targetGroup = previousData._vType === DATA_TYPE.GROUP ? previousData : previousData._parent
      if (!targetGroup) return
      try {
        if (oldData._parent.id === targetGroup.id) {
          await this.persistTreeRootOrder(targetGroup, oldData, previousData)
          return
        }

        const moveTo = previousData._vType === DATA_TYPE.GROUP
          ? 1
          : this.getRootComponentInsertOrder(targetGroup, previousData) + 1
        await this.handleMove(targetGroup, oldData, moveTo)
      } catch (error) {
        try {
          await this.fetchGroupList()
        } catch (refreshError) {
          // 请求层已统一提示刷新失败；Sortable 回调不再向外抛出异步异常。
        }
      }
    },
    restoreDragDom (tableSorter, newIndex, oldIndex) {
      const sortItems = tableSorter.toArray()
      const movedItem = sortItems.splice(newIndex, 1)
      if (!movedItem.length) return
      sortItems.splice(oldIndex, 0, movedItem[0])
      tableSorter.sort(sortItems)
    },
    findDragRow (element) {
      if (!element || !element.classList) return null
      return _.find(this.visibleGroupQuestionList, row => element.classList.contains(row._id)) ||
        _.find(this.flatGroupQuestionList, row => element.classList.contains(row._id))
    },
    isInsideQuestionTreeBoundary (previousData, nextData) {
      return previousData && nextData &&
        previousData._vType === DATA_TYPE.QUESTION &&
        nextData._vType === DATA_TYPE.QUESTION &&
        previousData._parent.id === nextData._parent.id &&
        previousData._treeRootKey === nextData._treeRootKey
    },
    getRootComponentInsertOrder (group, row) {
      const componentKeys = []
      _.forEach(group.children, question => {
        const componentKey = question._treeRootKey
        if (!_.includes(componentKeys, componentKey)) {
          componentKeys.push(componentKey)
        }
      })
      const targetIndex = _.indexOf(componentKeys, row._treeRootKey)
      if (targetIndex < 0) return 0
      return _.reduce(componentKeys.slice(0, targetIndex + 1), (count, componentKey) => {
        return count + _.filter(group.children, question => question._treeRootKey === componentKey).length
      }, 0)
    },
    async persistTreeRootOrder (group, movedRoot, previousData) {
      const roots = _.filter(group.children, question => question._treeDepth === 0)
      const movedIndex = _.findIndex(roots, question => this.getQuestionKey(question) === this.getQuestionKey(movedRoot))
      if (movedIndex < 0 || (previousData._vType === DATA_TYPE.QUESTION && previousData._treeRootKey === movedRoot._treeRootKey)) {
        return
      }
      const reorderedRoots = [...roots]
      reorderedRoots.splice(movedIndex, 1)
      let targetIndex = 0
      if (previousData._vType === DATA_TYPE.QUESTION) {
        targetIndex = _.findIndex(reorderedRoots, question => question._treeRootKey === previousData._treeRootKey)
        targetIndex = targetIndex < 0 ? reorderedRoots.length : targetIndex + 1
      }
      reorderedRoots.splice(targetIndex, 0, movedRoot)

      const reorderedQuestions = reorderRootSubtrees(
        group.children,
        reorderedRoots,
        () => _.uniqueId('template_tree_'))
      const flatQuestions = this.flattenQuestions(reorderedQuestions)
      const questionIdMap = this.buildQuestionIdMap(flatQuestions)
      const payloadQuestions = _.map(flatQuestions, questionData => ({
        ...this.buildQuestionSavePayload(questionData, group.id, questionIdMap),
        id: questionData.id,
        _vid: questionData._vid,
        mode: 'tree'
      }))

      this.loading = true
      try {
        await this.saveQuestionTemplateTree({
          group_id: group.id,
          questions: payloadQuestions,
          original_question_ids: _.chain(group.children).map('id').compact().value()
        })
        await this.fetchGroupList()
      } finally {
        this.loading = false
      }
    },
    canDragQuestion (question) {
      return question._vType === DATA_TYPE.QUESTION && question._treeDepth === 0
    },
    isTreeStatusLocked (row) {
      return row._vType === DATA_TYPE.QUESTION && row._treeDepth > 0
    },
    getTreeTriggerLabel (row) {
      const rowKey = String(row.id || row._vid)
      const parent = row._parent && _.find(row._parent.children, question =>
        _.some(question.options || [], option => String(getOptionChildKey(option)) === rowKey))
      const option = parent && _.find(parent.options || [], option => String(getOptionChildKey(option)) === rowKey)
      return this.getOptionDescription(option)
    },
    getQuestionTitle (question) {
      return question && question.title
    },
    getOptionDescription (option) {
      return option && option.description
    },
    hasQuestionChildren (question) {
      return Boolean(question && _.some(question.options || [], option => this.getOptionChildKey(option)))
    },
    isTreeGroupEnd (row) {
      if (!row._isTreeNode || !row._parent || !row._parent.children) {
        return true
      }
      const rowIndex = _.findIndex(row._parent.children, question => question._id === row._id)
      if (rowIndex < 0) {
        return true
      }
      const nextRow = row._parent.children[rowIndex + 1]
      return !nextRow || nextRow._treeRootKey !== row._treeRootKey
    },
    getRowClassName ({ row }) {
      return {
        'un-group-row': row.id === -1,
        'group-row': row._vType === DATA_TYPE.GROUP,
        'question-row': row._vType === DATA_TYPE.QUESTION,
        'tree-question-row': row._vType === DATA_TYPE.QUESTION && row._isTreeNode,
        'tree-root-row': row._vType === DATA_TYPE.QUESTION && row._isTreeNode && row._treeDepth === 0,
        'tree-child-row': row._vType === DATA_TYPE.QUESTION && row._treeDepth > 0,
        'tree-group-end-row': row._vType === DATA_TYPE.QUESTION && this.isTreeGroupEnd(row),
        [`${row._id}`]: true
      }
    },
    async fetchGroupList () {
      this.loading = true
      try {
        const resp = await this.getQuestionTemplateGroupList()
        if (resp) {
          this.groupList = resp.map(group => this.decorateGroupQuestionTree(parseGroup(group)))
          const visibleGroupKeys = new Set(this.groupList.map(group => group._id))
          this.expandedGroupKeys = this.expandedGroupKeys.filter(key => visibleGroupKeys.has(key))
          const ungrouped = _.find(this.groupList, { id: -1 })
          if (!this.hasInitializedExpandedGroups && this.groupList.length) {
            const firstNormalGroup = _.find(this.groupList, group => group.id !== -1)
            this.expandedGroupKeys = firstNormalGroup ? [firstNormalGroup._id] : []
          }
          if (ungrouped) this.expandedGroupKeys = _.uniq([...this.expandedGroupKeys, ungrouped._id])
          this.hasInitializedExpandedGroups = true
          this.searchGroupList()
        }
      } finally {
        this.loading = false
      }
    },
    searchGroupList () {
      if (this.search.groupId) {
        const group = _.find(this.groupList, { id: this.search.groupId })
        this.groupListSearchResult = [group]
        this.expandedGroupKeys = group ? _.uniq([group._id, ...this.getUngroupedExpandedKeys()]) : this.getUngroupedExpandedKeys()
      } else {
        this.groupListSearchResult = _.map(this.groupList)
        this.expandedGroupKeys = _.uniq([...this.expandedGroupKeys, ...this.getUngroupedExpandedKeys()])
      }
    },
    toTaskHistoryPage () {
      this.$router.push('/v-admin/open-question/template/management/task/list')
    },
    openAddGroupDialog () {
      this.groupEditor.isEdit = false
      this.groupEditor.group = null
      if (this.$refs.groupEditor) {
        this.$refs.groupEditor.clearData()
      }
      this.groupEditor.show = true
    },
    openAddQuestionDialog () {
      this.directQuestionEditor = { show: true, loading: false, isEdit: false, question: null, groupId: null, component: [], originalQuestionIds: [], templateLocked: false }
      this.$nextTick(() => {
        if (this.$refs.directQuestionEditor && this.$refs.directQuestionEditor.clearData) {
          this.$refs.directQuestionEditor.clearData()
        }
      })
    },
    handleExpandChange ({ row, expanded }) {
      if (row._vType !== DATA_TYPE.GROUP) return
      if (row.id === -1) {
        this.expandedGroupKeys = _.uniq([...this.expandedGroupKeys, row._id])
        return
      }
      const key = row._id
      this.expandedGroupKeys = expanded
        ? _.uniq([...this.expandedGroupKeys, key])
        : this.expandedGroupKeys.filter(item => item !== key)
    },
    getUngroupedExpandedKeys () {
      const ungrouped = _.find(this.groupList, { id: -1 })
      return ungrouped ? [ungrouped._id] : []
    },
    hasAvailableChildOption (row) {
      return this.canConfigureChildQuestion(row) && _.some(row.options || [], option =>
        !option.is_knockout && !option.child_question_id && !option.child_question_vid && !option.next_question_id && !option.child_question)
    },
    canConfigureChildQuestion (row) {
      return [QUESTION_TYPE.SELECT, QUESTION_TYPE.SINGLE_SELECT].includes(row.type)
    },
    getAddSubQuestionDisabledMessage (row) {
      if (!this.canConfigureChildQuestion(row)) return this.$t(`${this.i18nPrefix}.message.subQuestionUnsupportedParent`)
      if ((row._treeDepth || 0) >= MAX_TREE_DEPTH - 1) return this.$t(`${this.i18nPrefix}.message.subQuestionMaxDepth`)
      if (!this.hasAvailableChildOption(row)) return this.$t(`${this.i18nPrefix}.message.noAvailableChildOption`)
      return ''
    },
    handleAction (row, btn, btnName) {
      if (btnName === 'edit') return this.handleEdit(row)
      if (btnName === 'addSubQuestion') return this.openAddSubQuestion(row)
      if (btnName === 'delete') return this.deleteQuestionTree(row)
    },
    openAddSubQuestion (row) {
      this.openDirectChildEditor(row, null)
    },
    deleteQuestionTree (row) {
      if (this.deleteConfirmation.show || this.deleteConfirmation.loading) return
      const group = row._parent
      if (!group) return
      const result = removeQuestionSubtree(group.children || [], row)
      if (!result.removedKeys.length) return
      const questions = this.flattenQuestions(result.questions)
      const idMap = this.buildQuestionIdMap(questions)
      this.deleteConfirmation = {
        show: true,
        loading: false,
        count: result.removedKeys.length,
        pending: {
          groupId: group.id,
          questions: questions.map(question => ({ ...this.buildQuestionSavePayload(question, group.id, idMap), id: question.id, _vid: question._vid, mode: 'tree' })),
          originalQuestionIds: _.chain(group.children).map('id').compact().value()
        }
      }
    },
    async confirmDeleteQuestionTree () {
      const confirmation = this.deleteConfirmation
      if (!confirmation.show || confirmation.loading || !confirmation.pending) return
      confirmation.loading = true
      const pending = confirmation.pending
      try {
        await this.saveQuestionTemplateTree({
          group_id: pending.groupId,
          questions: pending.questions,
          original_question_ids: pending.originalQuestionIds
        })
        confirmation.show = false
        confirmation.pending = null
        confirmation.count = 0
        await this.fetchGroupList()
      } finally {
        confirmation.loading = false
      }
    },
    cancelDeleteQuestionTree () {
      if (this.deleteConfirmation.loading) return
      this.deleteConfirmation.show = false
      this.deleteConfirmation.pending = null
      this.deleteConfirmation.count = 0
    },
    handleEdit (row) {
      if (row._vType === DATA_TYPE.QUESTION) {
        if (row._treeDepth > 0) return this.openDirectChildEditor(this.findQuestionParent(row), row)
        this.openDirectQuestionEditor(row)
      } else {
        this.groupEditor.isEdit = true
        this.groupEditor.group = row
        this.groupEditor.show = true
      }
    },
    openDirectQuestionEditor (row) {
      const group = row._parent
      const component = this.getRelatedQuestionList(row, group)
      this.directQuestionEditor = {
        show: true,
        loading: false,
        isEdit: true,
        question: _.cloneDeep(row),
        groupId: group.id,
        component,
        originalQuestionIds: _.chain(component).map('id').compact().value(),
        templateLocked: false
      }
    },
    openDirectChildEditor (parent, child) {
      if (!parent) return
      const group = parent._parent
      const component = this.getRelatedQuestionList(child || parent, group)
      const attached = child && _.find(parent.options || [], option => String(this.getOptionChildKey(option)) === String(this.getQuestionKey(child)))
      this.childQuestionEditor = {
        show: true,
        loading: false,
        isEdit: Boolean(child),
        question: child ? _.cloneDeep(child) : null,
        parent,
        groupId: group.id,
        optionKey: attached ? String(attached.id || attached._vid) : null,
        originalOptionKey: attached ? String(attached.id || attached._vid) : null,
        component,
        originalQuestionIds: _.chain(component).map('id').compact().value()
      }
      if (!child) {
        this.$nextTick(() => {
          if (this.$refs.directChildQuestionEditor && this.$refs.directChildQuestionEditor.clearData) {
            this.$refs.directChildQuestionEditor.clearData()
          }
        })
      }
    },
    findQuestionParent (child) {
      const group = child && child._parent
      return group && _.find(group.children || [], question => _.some(question.options || [], option =>
        String(this.getOptionChildKey(option)) === String(this.getQuestionKey(child))))
    },
    async saveDirectComponent (groupId, component, originalQuestionIds) {
      const payload = this.buildDirectComponentPayload(groupId, component)
      await this.saveQuestionTemplateTree({
        group_id: groupId,
        questions: payload,
        original_question_ids: originalQuestionIds
      })
      await this.fetchGroupList()
    },
    buildDirectComponentPayload (groupId, component) {
      const flat = this.flattenQuestions(component)
      const idMap = this.buildQuestionIdMap(flat)
      return flat.map(question => ({
        ...this.buildQuestionSavePayload(question, groupId, idMap),
        id: question.id,
        _vid: question._vid,
        mode: 'tree'
      }))
    },
    async saveDirectQuestion () {
      const state = this.directQuestionEditor
      const editor = this.$refs.directQuestionEditor
      const data = await editor.submit()
      if (!data || !data.group_id) return
      state.loading = true
      try {
        const component = _.cloneDeep(state.component)
        if (state.isEdit) {
          const target = _.find(component, question => String(this.getQuestionKey(question)) === String(this.getQuestionKey(state.question)))
          if (!target) return
          _.assign(target, data)
          const groupId = data.group_id
          if (String(groupId) !== String(state.groupId)) {
            const fromGroup = _.find(this.groupList, group => String(group.id) === String(state.groupId))
            const toGroup = _.find(this.groupList, group => String(group.id) === String(groupId))
            if (!fromGroup || !toGroup) return
            const resp = await this.moveGroupQuestionTemplate({
              fromGroup,
              toGroup,
              question: target,
              moveTo: toGroup.children.length + 1,
              componentPayload: {
                questions: this.buildDirectComponentPayload(groupId, component),
                originalQuestionIds: state.originalQuestionIds
              }
            })
            if (resp) {
              this.applyGroupQuestionTemplates(fromGroup, resp.from_group.question_templates)
              this.applyGroupQuestionTemplates(toGroup, resp.to_group.question_templates)
            }
            // Move 已在后端事务中提交；先关闭并同步编辑态，避免刷新失败后重复提交旧来源分组。
            state.groupId = groupId
            state.show = false
            await this.fetchGroupList()
          } else {
            await this.saveDirectComponent(groupId, component, state.originalQuestionIds)
          }
        } else {
          data._vid = _.uniqueId('direct_question_')
          data.status = 1
          data.order = 1
          await this.saveDirectComponent(data.group_id, [data], [])
        }
        state.show = false
      } finally { state.loading = false }
    },
    async saveDirectChildQuestion () {
      const state = this.childQuestionEditor
      const data = await this.$refs.directChildQuestionEditor.submit()
      if (!data) return
      if (!state.optionKey) {
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.selectTriggerOptionRequired`))
        return
      }
      state.loading = true
      try {
        const component = _.cloneDeep(state.component)
        const parent = _.find(component, question => String(this.getQuestionKey(question)) === String(this.getQuestionKey(state.parent)))
        const targetOption = parent && _.find(parent.options || [], option => String(option.id || option._vid) === String(state.optionKey))
        if (!parent || !targetOption) return
        let child
        if (state.isEdit) {
          child = _.find(component, question => String(this.getQuestionKey(question)) === String(this.getQuestionKey(state.question)))
          if (!child) return
          _.assign(child, data)
          _.forEach(parent.options || [], option => {
            if (String(this.getOptionChildKey(option)) === String(this.getQuestionKey(child))) {
              option.child_question_id = null
              option.child_question_vid = null
              option.next_question_id = null
              option.child_question = null
            }
          })
        } else {
          child = { ...data, _vid: _.uniqueId('direct_child_'), order: component.length + 1 }
          component.push(child)
        }
        targetOption.child_question_id = child.id || null
        targetOption.child_question_vid = child._vid || null
        targetOption.next_question_id = child.id || null
        targetOption.child_question = child
        await this.saveDirectComponent(state.groupId, component, state.originalQuestionIds)
        state.show = false
      } finally { state.loading = false }
    },
    getRelatedQuestionList (row, parentGroup) {
      const allQuestions = _.map(parentGroup.question_templates, q => parseQuestion(q, parentGroup))
      const questionMap = {}
      const parentMap = {}

      _.forEach(allQuestions, question => {
        const questionKey = this.getQuestionKey(question)
        if (questionKey) {
          questionMap[questionKey] = question
        }
      })

      _.forEach(allQuestions, question => {
        _.forEach(question.options || [], option => {
          const childKey = this.getOptionChildKey(option)
          if (!childKey || !questionMap[childKey]) {
            return
          }
          const parentKey = this.getQuestionKey(question)
          if (!parentMap[childKey]) {
            parentMap[childKey] = []
          }
          parentMap[childKey].push(parentKey)
        })
      })

      const relatedKeys = new Set()
      const queue = [this.getQuestionKey(row)]

      while (queue.length) {
        const questionKey = queue.shift()
        if (!questionKey || relatedKeys.has(questionKey) || !questionMap[questionKey]) {
          continue
        }
        relatedKeys.add(questionKey)

        _.forEach(questionMap[questionKey].options || [], option => {
          const childKey = this.getOptionChildKey(option)
          if (childKey && !relatedKeys.has(childKey)) {
            queue.push(childKey)
          }
        })
        _.forEach(parentMap[questionKey] || [], parentKey => {
          if (parentKey && !relatedKeys.has(parentKey)) {
            queue.push(parentKey)
          }
        })
      }

      return _.filter(allQuestions, question => relatedKeys.has(this.getQuestionKey(question)))
    },
    async handleStatusChange (row) {
      if (this.isTreeStatusLocked(row)) {
        this.$message.warning(this.$t(`${this.i18nPrefix}.message.treeStatusChangeNotAllowed`))
        return
      }
      const updateFunc = row._vType === DATA_TYPE.QUESTION ? this.updateQuestionTemplate : this.updateQuestionTemplateGroup
      this.loading = true
      try {
        const newStatus = row.status === 1 ? 0 : 1
        const data = {
          questionId: row.id,
          data: {
            status: newStatus
          }
        }
        const resp = await updateFunc(data)
        if (resp) {
          await this.fetchGroupList()
        }
      } finally {
        this.loading = false
      }
    },
    async handleRequiredChange (row, required) {
      this.loading = true
      try {
        const resp = await this.updateQuestionTemplate({
          questionId: row.id,
          data: {
            required: Boolean(required)
          }
        })
        if (resp) {
          row.required = resp.required
        }
      } finally {
        this.loading = false
      }
    },
    async handleMove (group, question, moveTo) {
      if (moveTo === -1) {
        moveTo = group.children.length + 1
      }
      const parent = question._parent
      this.loading = true
      try {
        const resp = await this.moveGroupQuestionTemplate({
          fromGroup: parent,
          toGroup: group,
          question,
          moveTo
        })
        if (resp) {
          if (parent.id === group.id) {
            const responseGroup = resp.to_group || resp.from_group
            this.applyGroupQuestionTemplates(group, responseGroup.question_templates)
          } else {
            this.applyGroupQuestionTemplates(parent, resp.from_group.question_templates)
            this.applyGroupQuestionTemplates(group, resp.to_group.question_templates)
          }
        }
        this.searchGroupList()
      } finally {
        this.loading = false
      }
    },
    handleCancelGroup () {
      this.groupEditor.show = false
    },
    async handleSaveGroup () {
      this.groupEditor.loading = true
      try {
        const groupEditor = this.groupEditor
        if (!groupEditor.show) {
          return
        }
        const groupEditorRef = this.$refs.groupEditor
        const groupData = await groupEditorRef.submit()
        if (!groupData) {
          return
        }
        let data = groupData
        let saveFunc = this.createQuestionTemplateGroup
        if (groupEditor.isEdit) {
          data = {
            questionId: groupData.id,
            data: groupData
          }
          saveFunc = this.updateQuestionTemplateGroup
        }
        const resp = await saveFunc(data)
        if (resp) {
          if (groupEditor.isEdit) {
            const group = this.groupEditor.group
            group.name = resp.name
            group.cn_name = resp.name
          } else {
            const group = parseGroup(resp)
            this.groupList.splice(0, 0, group)
          }
          groupEditor.show = false
        }
        this.searchGroupList()
      } finally {
        this.groupEditor.loading = false
      }
    },
    getQuestionKey (question) {
      return getQuestionKey(question)
    },
    getOptionChildKey (option) {
      return getOptionChildKey(option)
    },
    applyGroupQuestionTemplates (group, questionTemplates) {
      group.question_templates = questionTemplates || []
      group.children = _.map(group.question_templates, question => parseQuestion(question, group))
      this.decorateGroupQuestionTree(group)
    },
    decorateGroupQuestionTree (group) {
      _.forEach(group.children || [], (question, index) => {
        // 树装饰会按根题重排展示；保留服务端稳定顺序供跨组落点与后端对齐。
        question._serverStablePosition = index
        question.options = assignMissingOptionVids(this.getQuestionKey(question), question.options)
      })
      group.children = decorateQuestionTreeRows(group.children || [])
      return group
    },
    buildQuestionIdMap (questions) {
      const questionIdMap = {}

      _.forEach(questions, question => {
        if (question.id) {
          questionIdMap[question.id] = question.id
        }
        if (question._vid && question.id) {
          questionIdMap[question._vid] = question.id
        }
      })

      return questionIdMap
    },

    resolveChildQuestionId (option, questionIdMap) {
      const childKey = option.child_question_id ||
        option.child_question_vid ||
        option.next_question_id

      return questionIdMap[childKey] || childKey || null
    },

    buildQuestionSavePayload (questionData, groupId, questionIdMap) {
      return {
        id: questionData.id,
        _vid: questionData._vid,
        title: questionData.title,
        type: questionData.type,
        duration: questionData.duration,
        order: questionData.order,
        required: questionData.required,
        need_update: questionData.need_update,
        group_id: groupId,
        options: _.map(questionData.options || [], option => {
          const childQuestionId = this.resolveChildQuestionId(option, questionIdMap)
          return {
            id: option.id,
            _vid: option._vid,
            description: option.description,
            order: option.order,
            is_knockout: option.is_knockout,
            child_question_id: childQuestionId,
            next_question_id: childQuestionId,
            child_question: null
          }
        })
      }
    },

    flattenQuestions (questions) {
      const flatList = []
      const processed = new Set()

      const flatten = (questionList, depth = 0) => {
        _.forEach(questionList, (q) => {
          const questionKey = q.id || q._vid
          if (!questionKey || processed.has(questionKey)) {
            return
          }
          processed.add(questionKey)

          const qData = {
            ...q,
            depth,
            order: flatList.length + 1
          }

          if (qData.options) {
            qData.options = _.map(qData.options, opt => ({
              id: opt.id,
              _vid: opt._vid,
              description: opt.description,
              order: opt.order,
              is_knockout: opt.is_knockout,
              child_question_id: opt.child_question_id || null,
              child_question_vid: opt.child_question_vid ||
                (opt.child_question && !opt.child_question_id
                  ? (opt.child_question.id || opt.child_question._vid || null)
                  : null),
              next_question_id: opt.next_question_id || null,
              child_question: null
            }))
          }
          flatList.push(qData)

          if (q.options) {
            _.forEach(q.options, opt => {
              if (opt.child_question) {
                flatten([opt.child_question], depth + 1)
              }
            })
          }
        })
      }
      flatten(questions)
      return _.map(flatList, (question, index) => ({
        ...question,
        order: index + 1
      }))
    },
    spanMethod ({ row, columnIndex }) {
      if (row.id !== -1) {
        return
      }
      if (columnIndex === 0) {
        return {
          rowspan: 1,
          colspan: 7
        }
      } else {
        return {
          rowspan: 0,
          colspan: 0
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.open-question-template-group-list {
  ::v-deep {
    .el-table__header-wrapper th.el-table__cell {
      height: 68px;
      padding: 0;
      background: #fafafa;

      .cell {
        color: #909399;
        font-size: 14px;
        font-weight: 500;
        line-height: 22px;
      }
    }

    .el-table__body-wrapper td.el-table__cell {
      vertical-align: top;
    }

    .el-table__body-wrapper tr.question-row td.el-table__cell {
      padding: 7px 0 !important;
    }

    .el-table__body-wrapper tr.tree-question-row:not(.tree-group-end-row) td.el-table__cell {
      border-bottom-color: transparent !important;
    }

    .management-title-cell .cell {
      overflow: visible !important;
      white-space: normal !important;
      text-overflow: clip !important;
      word-break: break-word !important;
    }

    .management-status-cell .cell,
    .management-required-cell .cell,
    .management-sort-cell .cell,
    .action-btn-column .cell {
      min-height: 28px;
      display: flex;
      align-items: center;
    }
  }

  .other-group {
    text-align: center;

    .title {
      margin: 0 20px;
    }
  }

  ::v-deep {
    .el-table {
      td.operate-btn-group {
        .cell {
          padding: 0 10px;
          display: flex;
          justify-content: space-between;
        }
      }

      .un-group-row {
        background-color: #fafafa;

        td.el-table__cell {
          &:hover {
            background-color: #fafafa;
          }

          .el-table__expand-icon {
            display: none;
          }
        }
      }
    }

    .btn-container {
      display: flex;
    }
  }

  .drag-btn {
    min-height: 28px;
    display: flex;
    align-items: center;

    .draggable-handle {
      cursor: pointer;
    }
  }

  .required-asterisk {
    margin-right: 3px;
    color: #f56c6c;
  }

  .question-tree-title {
    min-width: 0;
  }

  .question-title-line {
    display: flex;
    align-items: center;
    min-width: 0;
  }

  .question-tree-title:not(.is-child-question) .question-title-line {
    min-height: 28px;
  }

  .tree-gutter {
    display: inline-flex;
    align-self: stretch;
    flex: 0 0 auto;
  }

  .tree-meta-gutter {
    display: inline-flex;
    flex: 0 0 auto;
  }

  .tree-gutter-slot {
    position: relative;
    flex: 0 0 26px;
    width: 26px;

  }

  .tree-connector {
    &::before {
      position: absolute;
      top: -2px;
      left: 0;
      height: calc(50% + 2px);
      border-left: 1px solid #dcdfe6;
      content: '';
    }

    &::after {
      position: absolute;
      top: 50%;
      right: 8px;
      left: 0;
      border-top: 1px solid #dcdfe6;
      content: '';
    }
  }

  .question-title-text {
    min-width: 0;
    overflow-wrap: anywhere;
    color: #606266;
    font-size: 14px;
    line-height: 22px;
  }

  .question-tree-meta {
    display: flex;
    align-items: flex-start;
    flex-wrap: wrap;
    margin-bottom: 4px;
  }

  .tree-trigger-label {
    display: inline-block;
    padding: 2px 8px;
    border: 1px solid #dcdfe6;
    border-radius: 2px;
    background: #fafafa;
    color: #909399;
    font-size: 10px;
    line-height: 10px;
    max-width: 100%;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .is-child-question .question-title-text {
    font-size: 12px;
    line-height: 22px;
  }

  .locked-status { min-height: 28px; display: inline-flex; align-items: center; cursor: not-allowed; }

  .group-row {
    .drag-btn {
      visibility: hidden;
    }
  }
}

</style>
